import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

export type PaymentType = 'advance' | 'remaining';
export type PaymentMethod = 'wompi' | 'cash' | 'transfer';
export type PeriodFilter = 'today' | 'week' | 'month' | 'year' | 'custom';

export interface PaymentRecord {
  id: string;
  appointment_id: string;
  amount: number;
  status: string;
  type: PaymentType;
  method: PaymentMethod;
  reference: string | null;
  created_at: string;
  appointment: {
    client_name: string;
    client_email: string;
    appointment_date: string;
    start_time: string;
    status: string;
  } | null;
}

export interface AccountingKPIs {
  totalRevenue: number;
  advanceRevenue: number;
  remainingRevenue: number;
  transactionCount: number;
  advanceCount: number;
  remainingCount: number;
}

export interface AccountingFilters {
  period: PeriodFilter;
  dateFrom: string;
  dateTo: string;
}

function getDateRange(period: PeriodFilter, dateFrom: string, dateTo: string): { from: string; to: string } {
  const now = new Date();

  if (period === 'custom') {
    return { from: dateFrom, to: dateTo };
  }

  if (period === 'today') {
    const today = now.toISOString().split('T')[0];
    return { from: `${today}T00:00:00`, to: `${today}T23:59:59` };
  }

  if (period === 'week') {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { from: monday.toISOString(), to: sunday.toISOString() };
  }

  if (period === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    return { from: start.toISOString(), to: end.toISOString() };
  }

  // year
  const start = new Date(now.getFullYear(), 0, 1);
  const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function useAccounting() {
  const today = new Date().toISOString().split('T')[0];

  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [kpis, setKpis] = useState<AccountingKPIs>({
    totalRevenue: 0,
    advanceRevenue: 0,
    remainingRevenue: 0,
    transactionCount: 0,
    advanceCount: 0,
    remainingCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AccountingFilters>({
    period: 'month',
    dateFrom: today,
    dateTo: today,
  });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { from, to } = getDateRange(filters.period, filters.dateFrom, filters.dateTo);

      const { data, error: fetchErr } = await supabase
        .from('payments')
        .select(`
          id,
          appointment_id,
          amount,
          status,
          type,
          method,
          reference,
          created_at,
          appointments!payments_appointment_id_fkey (
            client_name,
            client_email,
            appointment_date,
            start_time,
            status
          )
        `)
        .eq('status', 'paid')
        .gte('created_at', from)
        .lte('created_at', to)
        .order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;

      // Supabase returns the joined table as 'appointments' (plural, table name).
      // Normalize it to 'appointment' (singular) to match PaymentRecord type.
      const records: PaymentRecord[] = (data ?? []).map((row: Record<string, unknown>) => {
        const apptRaw = row.appointments as Record<string, unknown> | null;
        return {
          id: row.id as string,
          appointment_id: row.appointment_id as string,
          amount: row.amount as number,
          status: row.status as string,
          type: row.type as PaymentType,
          method: row.method as PaymentMethod,
          reference: row.reference as string | null,
          created_at: row.created_at as string,
          appointment: apptRaw
            ? {
                client_name: apptRaw.client_name as string,
                client_email: apptRaw.client_email as string,
                appointment_date: apptRaw.appointment_date as string,
                start_time: apptRaw.start_time as string,
                status: apptRaw.status as string,
              }
            : null,
        };
      });
      setPayments(records);

      // Compute KPIs from records
      const totalRevenue = records.reduce((sum, p) => sum + Number(p.amount), 0);
      const advancePayments = records.filter((p) => p.type === 'advance');
      const remainingPayments = records.filter((p) => p.type === 'remaining');
      const advanceRevenue = advancePayments.reduce((sum, p) => sum + Number(p.amount), 0);
      const remainingRevenue = remainingPayments.reduce((sum, p) => sum + Number(p.amount), 0);

      setKpis({
        totalRevenue,
        advanceRevenue,
        remainingRevenue,
        transactionCount: records.length,
        advanceCount: advancePayments.length,
        remainingCount: remainingPayments.length,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cargar los datos contables';
      setError(msg);
      console.error('useAccounting error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const updateFilters = useCallback((newFilters: Partial<AccountingFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  return {
    payments,
    kpis,
    loading,
    error,
    filters,
    updateFilters,
    refetch: fetchPayments,
  };
}
